import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  IGetDashboardGlobalTramites,
  IGetDashboardNumeroTramitesPorMesByRangoFecha,
  IGetDashboardTramitePorDependenciaByRangoFecha
} from '../../../../shared/interfaces/database/tramite';
import { TramRegistroService } from '../../../../services/database/tramRegistro.service';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ChartBarComponent, ChartBarConfig } from '../../components/chart-bar/chart-bar.component';
import {
  ChartDonutComponent,
  ChartDonutConfig,
  ChartDonutSlice
} from '../../components/chart-donut/chart-donut.component';
import { ChartLineComponent, ChartLineConfig } from '../../components/chart-line/chart-line.component';
import {
  ChartRadialGaugeComponent,
  ChartRadialGaugeConfig
} from '../../components/chart-radial-gauge/chart-radial-gauge.component';
import {HEX_COLOR_PALETTE} from "../../components/utils/color-palette";

@Component({
  selector: 'app-dashboard-general-tramites',
  standalone: true,
  imports: [
    TranslatePipe,
    ChartBarComponent,
    ChartDonutComponent,
    ChartLineComponent,
    ChartRadialGaugeComponent
  ],
  templateUrl: './dashboard-general-tramites.component.html',
  styleUrl: './dashboard-general-tramites.component.scss'
})
export class DashboardGeneralTramitesComponent implements OnInit, AfterViewInit, OnChanges {

  @Input() fechaInicial!: Date;
  @Input() fechaFinal!: Date;
  @Input() tramRecibidos: string = '';

  @ViewChild('chartBarDependencia') chartBarDependencia!: ChartBarComponent;
  @ViewChild('chartDonutTotal') chartDonutComponent!: ChartDonutComponent;
  @ViewChild('chartLineHistorico') chartLineHistorico!: ChartLineComponent;
  @ViewChild('chartBarTopEfectividad') chartBarTopEfectividad!: ChartBarComponent;
  @ViewChild('chartRadialGaugeEfectividad') chartRadialGaugeEfectividad!: ChartRadialGaugeComponent;

  barDependenciaConfig!: ChartBarConfig;
  chartDonutConfig!: ChartDonutConfig;
  lineHistoricoConfig!: ChartLineConfig;
  barTopEfectividadConfig!: ChartBarConfig;
  radialGaugeEfectividadConfig!: ChartRadialGaugeConfig;

  private dependenciasData: IGetDashboardTramitePorDependenciaByRangoFecha[] = [];
  private globalTramitesData: IGetDashboardGlobalTramites[] = [];
  private rawGlobalTramitesData: IGetDashboardGlobalTramites[] = [];
  private historicoMesData: IGetDashboardNumeroTramitesPorMesByRangoFecha[] = [];

  colors = HEX_COLOR_PALETTE

  constructor(private _tr: TramRegistroService) {
    this.initBarDependenciaConfig();
    this.initChartDonutConfig();
    this.initLineHistoricoConfig();
    this.initBarTopEfectividadConfig();
    this.initRadialGaugeEfectividadConfig();
  }

  //#region life cycle
  ngOnInit(): void {
    this.callDataSets();
  }

  ngAfterViewInit(): void {
    if (this.dependenciasData?.length && this.chartBarDependencia) {
      this.chartBarDependencia.load(this.dependenciasData);
    }
    if (this.dependenciasData?.length && this.chartBarTopEfectividad) {
      this.loadTopEfectividad(this.dependenciasData);
    }
    if (this.globalTramitesData?.length && this.chartDonutComponent) {
      this.loadChartDonut(this.globalTramitesData);
    }
    if (this.rawGlobalTramitesData?.length && this.chartRadialGaugeEfectividad) {
      this.loadEfectividadGlobal(this.rawGlobalTramitesData);
    }
    if (this.historicoMesData?.length && this.chartLineHistorico) {
      this.chartLineHistorico.load(this.historicoMesData);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isFirstChange = Object.values(changes).every(c => c.isFirstChange());
    if (!isFirstChange) {
      this.callDataSets();
    }
  }
  //#endregion life cycle

  //#region init
  private initChartDonutConfig(): void {
    this.chartDonutConfig = {
      chartId: 'donut-global-tramite',
      slices: [],
      centerLabel: '',
    };
  }

  private initRadialGaugeEfectividadConfig(): void {
    this.radialGaugeEfectividadConfig = {
      chartId: 'radial-gauge-efectividad-tramites',
      percentage: 0,
      centerSub: '0 / 0',
      height: 240,
    };
  }

  private initLineHistoricoConfig(): void {
    this.lineHistoricoConfig = {
      chartId: 'chart-historico-tramite',
      categoryField: 'mes',
      series: [
        { field: 'valor', name: 'Trámites', color: this.colors[0] },
      ],
      data: [],
    };
  }

  private initBarTopEfectividadConfig(): void {
    this.barTopEfectividadConfig = {
      chartId: 'chart-top-efectividad',
      categoryField: 'dependencia',
      showLegend: false,
      barHeight: 80,
      xLabelFormatter: (v: number) => `${v}%`,
      tooltipValueFormatter: (v: number) => `${v.toFixed(1)}%`,
      bars: [
        {
          field: 'efectividad',
          name: 'Efectividad',
          color: this.colors[0],
          showValueLabel: true,
          valueLabelFormatter: (v: number) => `${v.toFixed(1)}%`,
        },
      ],
      data: [],
    };
  }

  private initBarDependenciaConfig(): void {
    this.barDependenciaConfig = {
      chartId: 'chart-bar-dependencia',
      categoryField: 'dependencia',
      showLegend: true,
      barHeight: 55,
      xLabelFormatter: (v: number) => `${v}`,
      tooltipValueFormatter: (v: number) => `${v}`,
      bars: [
        { field: 'recibidos', name: 'Recibidos', color: this.colors[2], showValueLabel: true },
        { field: 'pendientes', name: 'Pendientes', color: this.colors[1], showValueLabel: true },
        { field: 'finalizados', name: 'Finalizados', color: this.colors[0], showValueLabel: true },
      ],
      data: [],
    };
  }
  //#endregion init

  //#region load
  private loadChartDonut(data: IGetDashboardGlobalTramites[]): void {
    if (!this.chartDonutComponent) return;
    const value: ChartDonutSlice[] = data.map((item, idx) => ({
      label: item.tipo,
      value: item.total,
      color: this.colors[idx % this.colors.length],
    }));
    const porcentaje = data?.find(it=> it.tipo.includes("finalizados"));

    this.chartDonutConfig.centerLabel = porcentaje ? `${porcentaje.total}` :'';
    this.chartDonutConfig.centerSub = 'Finalizados';
    this.chartDonutComponent.load(value);
  }

  private loadTopEfectividad(data: IGetDashboardTramitePorDependenciaByRangoFecha[]): void {
    if (!this.chartBarTopEfectividad) return;
    const topFive = (data ?? [])
      .map(item => ({
        dependencia: item.dependencia,
        efectividad: +(item.efectividad * 100).toFixed(1),
      }))
      .sort((a, b) => b.efectividad - a.efectividad)
      .slice(0, 5);
    this.chartBarTopEfectividad.load(topFive);
  }

  private loadEfectividadGlobal(values: IGetDashboardGlobalTramites[]): void {
    if (!this.chartRadialGaugeEfectividad) return;
    const recibidos = values.find(it => it.tipo === 'recibidos');
    const finalizados = values.find(it => it.tipo === 'finalizados');
    const recibidosTotal = recibidos?.total ?? 0;
    const finalizadosTotal = finalizados?.total ?? 0;
    const pct = recibidosTotal > 0 ? (finalizadosTotal / recibidosTotal) * 100 : 0;
    const pctRounded = +(pct.toFixed(1));

    this.chartRadialGaugeEfectividad.load({
      percentage: pctRounded,
      centerLabel: `${pctRounded}%`,
      centerSub: `${finalizadosTotal} / ${recibidosTotal} \n Trámites`,
    });
  }
  //#endregion load

  //#region Data loading methods
  getHistoricoTramitebyMes(): void {
    firstValueFrom(
      this._tr.getDashboardNumeroTramitesPorMesByRangoFecha(
        this.castToString(this.fechaInicial),
        this.castToString(this.fechaFinal)
      )
    ).then(response => {
      if (!response) return;
      const mapped = response.map(item => ({
        mes: item.mes,
        valor: (item as any).valor ?? (item as any).value ?? 0,
      }));
      this.historicoMesData = mapped as any;
      this.chartLineHistorico?.load(mapped);
    }).catch(error => {
      console.error(error);
    });
  }

  getDashboardTramitePorDependencia(): void {
    firstValueFrom(
      this._tr.getDashboardTramitePorDependenciaByRangoFecha(
        this.castToString(this.fechaInicial),
        this.castToString(this.fechaFinal)
      )
    ).then(response => {
      if (!response) return;
      this.dependenciasData = response;
      this.chartBarDependencia?.load(response);
      this.loadTopEfectividad(response);
    }).catch(error => {
      console.error(error);
    });
  }

  getGlobalTramites(): void {
    const startDate = this.castToString(this.fechaInicial);
    const endDate = this.castToString(this.fechaFinal);

    firstValueFrom(
      this._tr.getDashboardGlobalTramitesByRangoFecha(startDate, endDate)
    ).then(value => {
      if (!value) return;
      this.rawGlobalTramitesData = value;
      const data: IGetDashboardGlobalTramites[] = value.filter(item => item.tipo !== 'recibidos');
      this.globalTramitesData = data;
      this.loadChartDonut(data);
      this.loadEfectividadGlobal(value);
    }).catch(error => {
      console.error(error);
    });
  }
  //#endregion Data loading methods

  //#region Helpers

  castToString(date: Date): string {
    return date.toISOString().substring(0, date.toISOString().indexOf('T'));
  }

  callDataSets(): void {
    this.getDashboardTramitePorDependencia();
    this.getHistoricoTramitebyMes();
    this.getGlobalTramites();
  }
//#endregion Helpers
}
